import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, appId } from './utils/firebase';

import PublicFront from './views/PublicFront';
import LoginGate from './components/LoginGate';
import TertiaryHub from './components/TertiaryHub';
import UnauthorizedSplash from './views/UnauthorizedSplash';
import CrewLogin from './views/CrewLogin';
import CrewPortal from './views/CrewPortal';

const wantsCrew = () => window.location.hash === '#crew';
const clearCrewHash = () => { if (wantsCrew()) window.history.replaceState(null, '', window.location.pathname + window.location.search); };

const App = () => {
    const [viewState, setViewState] = useState('checking');
    const [handle, setHandle] = useState('');
    const [clearance, setClearance] = useState('LEVEL 1');
    const [role, setRole] = useState('');
    const [uid, setUid] = useState('');

    // Decides where a signed-in person goes: staff to the hub, crew to the crew portal, everyone else to a holding page.
    const routeUser = async (u) => {
        if (!u) return;
        try {
            const snap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', u.uid));
            if (snap.exists()) {
                // Registered but not approved yet: end the session. (A missing profile is NOT treated this way,
                // because a brand-new registration signs in a moment before its profile document is written.)
                if (snap.data().status === 'PENDING') { await signOut(auth); return; }
                const c = snap.data().clearance;
                setHandle(snap.data().handle || 'Operative');
                setClearance(c || 'LEVEL 1');
                setUid(u.uid);
                setRole(snap.data().role || '');
                if (c === 'FIELD OP' || c === 'LEVEL 0') {
                    setViewState('unauthorized'); return;
                }
                if (c === 'CREW') {
                    clearCrewHash(); setViewState('crew'); return;
                }
            } else {
                setHandle(u.email ? u.email.split('@')[0] : 'Admin');
                setClearance('LEVEL 1');
            }
        } catch (e) {
            console.error("Auth check issue:", e);
            setHandle(u.email ? u.email.split('@')[0] : 'Admin');
            setClearance('LEVEL 1');
        }
        clearCrewHash();
        setViewState('hub');
    };

    useEffect(() => {
        const initAuth = async () => {
            // Support for Canvas injected token (if present)
            if (typeof window.__initial_auth_token !== 'undefined' && window.__initial_auth_token) {
                try { await signInWithCustomToken(auth, window.__initial_auth_token); }
                catch (err) { console.warn("Canvas token auth failed", err); }
            }
        };
        initAuth();

        const unsub = onAuthStateChanged(auth, async (u) => {
            if (u) {
                // a sign-in screen is in the middle of creating a profile: let it finish before deciding where to go
                if (window.__crewAuthBusy) return;
                await routeUser(u);
            } else {
                setViewState(prev => (prev === 'login' || prev === 'crewlogin') ? prev : (wantsCrew() ? 'crewlogin' : 'public'));
            }
        });
        return () => unsub();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (viewState === 'checking') return <div className="min-h-screen bg-black flex items-center justify-center text-gray-500 font-mono text-xs">SYNCHRONIZING...</div>;

    const handleLogout = async () => {
        const wasCrew = viewState === 'crew';
        await signOut(auth);
        if (wasCrew) { window.location.hash = 'crew'; setViewState('crewlogin'); } else setViewState('public');
    };

    const openCrewLogin = () => { window.location.hash = 'crew'; setViewState('crewlogin'); };
    const closeCrewLogin = () => { clearCrewHash(); setViewState('public'); };

    return (
        <div className="antialiased font-sans h-screen w-full flex flex-col overflow-hidden bg-black text-white">
            {viewState === 'public' && <PublicFront onBreach={() => setViewState('login')} onCrew={openCrewLogin} />}
            {viewState === 'login' && <LoginGate onSuccess={(target) => (target === 'unauthorized' ? setViewState('unauthorized') : routeUser(auth.currentUser))} onClose={() => setViewState('public')} setHandle={setHandle} setClearance={setClearance} />}
            {viewState === 'crewlogin' && <CrewLogin onClose={closeCrewLogin} onDone={() => routeUser(auth.currentUser)} />}
            {viewState === 'hub' && <TertiaryHub onExit={handleLogout} handle={handle} clearance={clearance} />}
            {viewState === 'crew' && <CrewPortal onExit={handleLogout} handle={handle} role={role} uid={uid} />}
            {viewState === 'unauthorized' && <UnauthorizedSplash onExit={handleLogout} />}
        </div>
    );
};

export default App;
